import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { JSXElementConstructor, ReactNode, useMemo, useState } from "react";
import {
  RedoList,
  RedoListItem,
} from "../../arbiter-components/list/redo-list";
import { Flex } from "../../flex";
import * as segmentBuilderCss from "./segment-builder.module.css";

export interface Category<ITEM extends Item> {
  name: string;
  Icon: JSXElementConstructor<any>;
  items: ITEM[];
}

export interface Item {
  name: string;
}

export const TrifoldSelector = genericMemo(function TrifoldSelector<
  CATEGORY extends Category<ITEM>,
  ITEM extends Item,
>({
  categories,
  children,
  selectedItem,
  setSelectedItem,
}: {
  categories: CATEGORY[];
  children: (item: ITEM) => ReactNode;
  selectedItem: ITEM | undefined;
  setSelectedItem: (item: ITEM | undefined) => void;
}) {
  const categoryOptions: RedoListItem<CATEGORY>[] = useMemo(() => {
    return categories.map<RedoListItem<CATEGORY>>((category) => {
      return {
        type: "text",
        id: category.name,
        text: category.name,
        leadingItem: { type: "icon", Icon: category.Icon },
        value: category,
      };
    });
  }, [categories]);

  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(
    () => {
      if (selectedItem) {
        const index = categories.findIndex((category) => {
          return category.items.some((item) => {
            return item.name === selectedItem.name;
          });
        });
        if (index !== -1) {
          return index;
        }
      }
      return 0;
    },
  );

  const itemOptions: RedoListItem<ITEM>[] = useMemo(() => {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return categories[selectedCategoryIndex ?? selectedCategoryIndex].items.map<
      RedoListItem<ITEM>
    >((item) => {
      return { type: "text", id: item.name, text: item.name, value: item };
    });
  }, [categories, selectedCategoryIndex]);

  const [focusedItemIndex, setFocusedItemIndex] = useState<
    number | undefined
  >();

  const handleItemSelected = useHandler(({ index }: { index: number }) => {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const item = categories[selectedCategoryIndex].items[index];
    setSelectedItem(item);
  });

  const handleUpdateSelectedCategory = useHandler(
    ({ index }: { index: number }) => {
      if (index === selectedCategoryIndex) {
        return;
      }
      setSelectedItem(undefined);
      setSelectedCategoryIndex(index);
    },
  );

  const focusedItem =
    focusedItemIndex !== undefined
      ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        categories[selectedCategoryIndex].items[focusedItemIndex]
      : undefined;

  const itemToDisplay = focusedItem || selectedItem;

  const selectedCategories = useMemo(() => {
    if (selectedCategoryIndex === undefined) {
      return [];
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const category = categoryOptions[selectedCategoryIndex].value.name;
    if (!category) {
      return [];
    }
    return [category];
  }, [selectedCategoryIndex, categoryOptions]);

  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      borderTopWidth="1px"
      className={segmentBuilderCss.trifoldSelectorBody}
      dir="row"
      gap="none"
      height="full"
    >
      <Flex
        borderColor="secondary"
        borderRightWidth="1px"
        borderStyle="solid"
        dir="column"
        flex="1 1 33.333%"
        height="full"
        px="lg"
        py="xl"
      >
        <div className={segmentBuilderCss.trifoldSelectorList}>
          <RedoList
            focusedIndex={selectedCategoryIndex}
            items={categoryOptions}
            itemSelected={handleUpdateSelectedCategory}
            refToListenTo={null}
            selectedItems={selectedCategories}
            selectionVariant="none"
            setFocusedIndex={(action) => {
              const newValue =
                typeof action === "function"
                  ? action(selectedCategoryIndex)
                  : action;
              if (newValue === undefined) {
                return;
              }
              setSelectedCategoryIndex(newValue);
            }}
            size="sm"
          />
        </div>
      </Flex>
      <Flex
        borderColor="secondary"
        borderRightWidth="1px"
        borderStyle="solid"
        dir="column"
        flex="1 1 33.333%"
        height="full"
        px="lg"
        py="xl"
      >
        <div className={segmentBuilderCss.trifoldSelectorList}>
          <RedoList
            focusedIndex={focusedItemIndex}
            items={itemOptions}
            itemSelected={handleItemSelected}
            refToListenTo={null}
            selectedItems={selectedItem?.name}
            selectionVariant="none"
            setFocusedIndex={setFocusedItemIndex}
            size="sm"
          />
        </div>
      </Flex>
      <Flex
        bgColor="secondary"
        dir="column"
        flex="1 1 33.333%"
        gap="lg"
        height="full"
        px="lg"
        py="xl"
      >
        {itemToDisplay && children(itemToDisplay)}
      </Flex>
    </Flex>
  );
});
